import {
  modelCatalogueOf,
  panoVariantsOf,
  servedWorldsOf,
  validateLesson,
  withCurrentWorldNames,
  type Lesson,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './api/served-catalogue.js';
import type { LoadedLesson } from './api/lessons-client.js';
import { mayPicture } from './engine/picture-step.js';
import { parentOrigin } from './world-viewer.js';

export { parentOrigin };

/**
 * A lesson's own picture (ADR-098): the headset app at `?lessonPicture=<ticket>`, in an iframe of the
 * console -- out of sight when the console makes a lesson's first picture, in view in "Kies plaatje".
 * The ordinary player draws the lesson, so the card shows what a class sees: its world (or the
 * studio), its models, as a step leaves them, from where the pupil stands then, without the lectern,
 * the labels or the points. No XR, no socket, no sound, nothing recorded; the ticket -- single use, a
 * minute -- fetches one lesson version and its signed media, as a preview ticket does (ADR-029), and
 * names the one console origin this page talks to. It answers by postMessage:
 *
 * | Console → page | Page → console |
 * | --- | --- |
 * | – | `{type: 'ready', steps: [{id, title}], suggested}` once the lesson is in |
 * | `{type: 'capture', stepId?}` | `{type: 'picture', jpeg: Blob, stepId}` 1280 × 720 JPEG ≤ 1 MB |
 * | – | `{type: 'trouble', text}` in Dutch |
 *
 * `steps` are those a picture may be of (no quiz or prediction); `suggested` the step
 * engine/picture-step.ts chooses. The console uploads the JPEG (`PUT …/picture`): a page on the
 * headset's origin never holds a person's token, so it never stores anything itself.
 */

/** The picture: 1280 × 720 JPEG, at most 1 MB (the API refuses more). */
export const LESSON_PICTURE = {
  width: 1280,
  height: 720,
  quality: 0.85,
  /** Tried when the first JPEG is over `maxBytes`. */
  fallbackQuality: 0.7,
  maxBytes: 1024 * 1024,
} as const;

/** The teacher's words, in Dutch. */
export const LESSON_PICTURE_TEXT = {
  loading: 'De les wordt geladen…',
  gone: 'Deze link is verlopen. Open de les opnieuw.',
  failed: 'Deze les kon niet worden geladen.',
  drawing: 'Het plaatje lukte niet. Probeer het opnieuw.',
  /** Not everything of the lesson loaded: no picture of a scene that is not the lesson's (ADR-098). */
  incomplete:
    'Niet alles van de les is geladen, dus er is geen plaatje gemaakt. Probeer het later opnieuw.',
  /** Every step is a question: nothing to draw. */
  noSteps: 'Deze les heeft geen stap om een plaatje van te maken.',
  tooLarge: 'Het plaatje werd te groot.',
} as const;

/** What `GET /v1/lesson-picture/{ticket}` answers, read and validated. */
export type LessonPictureAnswer = {
  readonly loaded: LoadedLesson;
  readonly consoleOrigin: string;
};

export type LessonPictureOutcome =
  | { kind: 'ready'; answer: LessonPictureAnswer }
  | { kind: 'gone' }
  | { kind: 'failed'; detail: string };

export type LessonPictureStart = { kind: 'ticket'; ticket: string } | { kind: 'spent' };

/** Marks the history entry a ticket was read from, as the world viewer does. */
const SPENT = 'vrlearningLessonPicture';

/** Reads `?lessonPicture=<ticket>` and takes it off the address at once (a used ticket is worthless). */
export function takeLessonPictureRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'replaceState' | 'state'> | undefined = globalThis.history,
): LessonPictureStart | undefined {
  let url: URL | undefined;
  try {
    url = new URL(place?.href ?? '');
  } catch {
    url = undefined;
  }
  const ticket = url?.searchParams.get('lessonPicture');
  if (url && ticket) {
    history?.replaceState({ [SPENT]: true }, '', url.pathname);
    return { kind: 'ticket', ticket };
  }
  const state: unknown = history?.state;
  if (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[SPENT] === true
  )
    return { kind: 'spent' };
  return undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** An origin and nothing else (`https://host:port`): postMessage's target is never `*` or a URL. */
function isOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin !== 'null' && url.origin === value;
  } catch {
    return false;
  }
}

/** Redeems the ticket, uncached, and validates the lesson as every other one is. */
export async function fetchLessonPicture(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<LessonPictureOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/v1/lesson-picture/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  if (!isRecord(body)) return { kind: 'failed', detail: 'onvolledig antwoord' };
  const origin = body['consoleOrigin'];
  if (typeof origin !== 'string' || !isOrigin(origin)) {
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  }
  const worlds = servedWorldsOf(body['worlds']);
  const catalogue = catalogueOfServed(worlds);
  const result = validateLesson(body['lesson'], { worlds: catalogue });
  if (!result.valid) {
    const first = result.errors[0];
    return {
      kind: 'failed',
      detail: `les is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`.trim(),
    };
  }
  const media = isRecord(body['media']) ? body['media'] : {};
  return {
    kind: 'ready',
    answer: {
      consoleOrigin: origin,
      loaded: {
        lesson: withCurrentWorldNames(result.lesson, catalogue),
        media: Object.fromEntries(
          Object.entries(media).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string',
          ),
        ),
        // Silent: a picture has no voice.
        cues: {},
        variants: panoVariantsOf(body['variants']),
        models: modelCatalogueOf(body['models']),
        worlds,
      },
    },
  };
}

/** The steps a picture may be of, as the console lists them in "Kies plaatje". */
export function pictureSteps(lesson: Lesson): { id: string; title: string }[] {
  return lesson.steps.filter(mayPicture).map((step) => ({ id: step.id, title: step.title }));
}

/** What the page draws with (main.ts: the ordinary player; a fake in tests). */
export type LessonPictureRenderer = {
  /** The lesson built, its world, models and photos in; throws when it cannot be. */
  load(): Promise<void>;
  /** The step chosen when the console names none (engine/picture-step.ts). */
  suggested(): string;
  /**
   * The step's settled scene from the pupil's place, as a 1280 × 720 JPEG ≤ 1 MB; a
   * `SceneIncomplete` when the scene is not the lesson's as it should be.
   */
  capture(stepId: string): Promise<Blob>;
};

export type LessonPictureMessage =
  | { type: 'ready'; steps: { id: string; title: string }[]; suggested: string }
  | { type: 'trouble'; text: string }
  | { type: 'picture'; jpeg: Blob; stepId: string };

/**
 * The page's side of the conversation: builds the lesson, says `ready`, and answers each `capture`
 * -- only from the console the ticket names, one at a time. A step it does not know, or one that is
 * a question, is drawn as the suggested one; the answer says which it drew.
 */
export class LessonPictureHost {
  readonly #answer: LessonPictureAnswer;
  readonly #renderer: LessonPictureRenderer;
  readonly #post: (message: LessonPictureMessage) => void;
  #built = false;
  #queue: Promise<void> = Promise.resolve();

  constructor(input: {
    answer: LessonPictureAnswer;
    renderer: LessonPictureRenderer;
    post: (message: LessonPictureMessage) => void;
  }) {
    this.#answer = input.answer;
    this.#renderer = input.renderer;
    this.#post = input.post;
  }

  async start(): Promise<void> {
    if (pictureSteps(this.#answer.loaded.lesson).length === 0) {
      this.#post({ type: 'trouble', text: LESSON_PICTURE_TEXT.noSteps });
      return;
    }
    try {
      await this.#renderer.load();
    } catch {
      this.#post({ type: 'trouble', text: LESSON_PICTURE_TEXT.failed });
      return;
    }
    this.#built = true;
    this.#post({
      type: 'ready',
      steps: pictureSteps(this.#answer.loaded.lesson),
      suggested: this.#renderer.suggested(),
    });
  }

  /** A message from the window: acted on only from the console's origin. */
  receive(event: { origin: string; data: unknown }): Promise<void> {
    if (event.origin !== this.#answer.consoleOrigin) return Promise.resolve();
    const data = event.data;
    if (!isRecord(data)) return Promise.resolve();
    this.#queue = this.#queue
      .then(() => this.#handle(data))
      .catch((error: unknown) =>
        this.#post({
          type: 'trouble',
          text:
            error instanceof SceneIncomplete
              ? LESSON_PICTURE_TEXT.incomplete
              : error instanceof Error && error.message === LESSON_PICTURE_TEXT.tooLarge
                ? LESSON_PICTURE_TEXT.tooLarge
                : LESSON_PICTURE_TEXT.drawing,
        }),
      );
    return this.#queue;
  }

  async #handle(message: Record<string, unknown>): Promise<void> {
    if (message['type'] !== 'capture' || !this.#built) return;
    const asked = message['stepId'];
    const allowed = pictureSteps(this.#answer.loaded.lesson).map((step) => step.id);
    const stepId =
      typeof asked === 'string' && allowed.includes(asked) ? asked : this.#renderer.suggested();
    const jpeg = await this.#renderer.capture(stepId);
    this.#post({ type: 'picture', jpeg, stepId });
  }
}

/**
 * The canvas as a JPEG within `LESSON_PICTURE.maxBytes`: at the usual quality, else once more at a
 * lower one; refused when even that is too large.
 */
export async function pictureJpeg(encode: (quality: number) => Promise<Blob>): Promise<Blob> {
  const first = await encode(LESSON_PICTURE.quality);
  if (first.size <= LESSON_PICTURE.maxBytes) return first;
  const second = await encode(LESSON_PICTURE.fallbackQuality);
  if (second.size <= LESSON_PICTURE.maxBytes) return second;
  throw new Error(LESSON_PICTURE_TEXT.tooLarge);
}

/**
 * A scene that did not load as the lesson has it (ADR-098): its world fell back to the studio, a world
 * file, a poster's photo, a model or the step's 360° photo is missing, or nothing was drawn at all.
 * Never stored as the lesson's picture: the page says so instead, and the console keeps what it had.
 */
export class SceneIncomplete extends Error {
  constructor(readonly problems: readonly string[]) {
    super(`scene incomplete: ${problems.join(', ')}`);
  }
}

/** A picture all of one colour (within a few steps): what a lost WebGL context draws. */
export function isBlank(
  canvas: Pick<HTMLCanvasElement, 'width' | 'height' | 'getContext'>,
  tolerance = 6,
): boolean {
  const context = canvas.getContext('2d');
  if (!context) return true;
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  let low = 255;
  let high = 0;
  // Every 97th pixel: enough to see a scene, cheap on 1280 × 720.
  for (let i = 0; i < data.length; i += 4 * 97) {
    const value = (data[i]! + data[i + 1]! + data[i + 2]!) / 3;
    low = Math.min(low, value);
    high = Math.max(high, value);
  }
  return high - low <= tolerance;
}
